import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Alert,
    Stack,
    LinearProgress,
    CircularProgress,
    TablePagination,
    Tooltip,
    Snackbar,
    TextField,
    InputAdornment,
    Chip,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { Title, useGetIdentity } from 'react-admin';
import { supabase } from '../providers/supabase/supabase';
import { usePapaParse } from '../misc/usePapaParse';
import {
    useHailProductDefsImport,
    HailProductDefsCsvRow,
} from './useHailProductDefsImport';

type HailProductDefRow = {
    id: number;
    hail_product: string;
    definition: string;
    hail_product_alias: string;
    created_at: string;
};

type SortField = 'hail_product';
type SortDir = 'asc' | 'desc';

export const HailProductDefsPage = () => {
    const { identity } = useGetIdentity();
    const isAdmin = !!(identity as any)?.administrator;

    const [rows, setRows] = useState<HailProductDefRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadOpen, setUploadOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortField, setSortField] = useState<SortField>('hail_product');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    const [search, setSearch] = useState('');
    const [snackMsg, setSnackMsg] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const PAGE_SIZE = 1000;
            let allData: HailProductDefRow[] = [];
            let from = 0;
            let hasMore = true;

            while (hasMore) {
                const { data, error: fetchError } = await supabase
                    .from('hail_product_definitions')
                    .select('*')
                    .order('hail_product', { ascending: true })
                    .range(from, from + PAGE_SIZE - 1);

                if (fetchError) throw fetchError;
                const page = (data as HailProductDefRow[]) ?? [];
                allData = allData.concat(page);
                hasMore = page.length === PAGE_SIZE;
                from += PAGE_SIZE;
            }

            setRows(allData);
        } catch (err: any) {
            console.error('Failed to fetch hail product definitions:', err);
            setError('Failed to load hail product definitions data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const displayRows = useMemo(() => {
        let filtered = rows;
        if (search) {
            const q = search.toLowerCase();
            filtered = filtered.filter(
                r =>
                    r.hail_product.toLowerCase().includes(q) ||
                    (r.hail_product_alias || '').toLowerCase().includes(q)
            );
        }

        return [...filtered].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            const cmp = String(aVal).localeCompare(String(bVal));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [rows, search, sortField, sortDir]);

    const handleSort = () => {
        setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    };

    const handleClearAll = async () => {
        if (
            !window.confirm(
                'Are you sure you want to delete ALL hail product definitions? This cannot be undone.'
            )
        )
            return;

        try {
            const { error: delError } = await supabase
                .from('hail_product_definitions')
                .delete()
                .neq('id', 0);
            if (delError) throw delError;
            setRows([]);
        } catch (err: any) {
            console.error('Failed to clear hail product definitions:', err);
            setError('Failed to clear hail product definitions data.');
        }
    };

    const handleDownloadSample = () => {
        const csv =
            'Hail Product,Definition,Hail Product Alias\n' +
            'Basic Hail,"Covers direct physical loss from hail damage","BH, Basic"\n' +
            'Production Hail,"Covers yield reduction due to hail","PH, Prod Hail"\n';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'hail_product_definitions_sample.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Title title="Hail Product Definitions" />

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Typography variant="h5" fontWeight={600}>
                    Hail Product Definitions
                </Typography>
                {isAdmin && (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Download sample CSV">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadSample}
                            >
                                Sample CSV
                            </Button>
                        </Tooltip>
                        {rows.length > 0 && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={handleClearAll}
                            >
                                Clear All
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={() => setUploadOpen(true)}
                        >
                            Upload CSV
                        </Button>
                    </Stack>
                )}
            </Stack>

            {/* Search bar */}
            {rows.length > 0 && (
                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 2 }}
                >
                    <TextField
                        size="small"
                        placeholder="Search hail products..."
                        value={search}
                        onChange={e => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        sx={{ minWidth: 300 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {displayRows.length} record
                        {displayRows.length !== 1 ? 's' : ''}
                    </Typography>
                </Stack>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 6,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : rows.length === 0 ? (
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <UploadIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                        No hail product definitions yet
                    </Typography>
                    {isAdmin ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Upload a CSV file to populate the hail product
                                definitions table.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<UploadIcon />}
                                onClick={() => setUploadOpen(true)}
                            >
                                Upload CSV
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body2">
                            An admin has not uploaded hail product definitions
                            yet.
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            width: 280,
                                        }}
                                        sortDirection={sortDir}
                                    >
                                        <TableSortLabel
                                            active
                                            direction={sortDir}
                                            onClick={handleSort}
                                        >
                                            Hail Product
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        Definition
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        Hail Product Alias
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayRows
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map(row => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>
                                                {row.hail_product || '\u2014'}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {row.definition || '\u2014'}
                                            </TableCell>
                                            <TableCell>
                                                {row.hail_product_alias
                                                    ? row.hail_product_alias
                                                          .split(',')
                                                          .map(a => a.trim())
                                                          .filter(Boolean)
                                                          .map(alias => (
                                                              <Chip
                                                                  key={alias}
                                                                  label={alias}
                                                                  size="small"
                                                                  sx={{
                                                                      mr: 0.5,
                                                                      mb: 0.5,
                                                                  }}
                                                              />
                                                          ))
                                                    : '\u2014'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={displayRows.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}

            <UploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onComplete={() => {
                    setUploadOpen(false);
                    fetchData();
                }}
            />

            <Snackbar
                open={!!snackMsg}
                autoHideDuration={2000}
                onClose={() => setSnackMsg('')}
                message={snackMsg}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            />
        </Box>
    );
};

// ---------- Upload Dialog ----------

function UploadDialog({
    open,
    onClose,
    onComplete,
}: {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
}) {
    const { processBatch } = useHailProductDefsImport();
    const { importer, parseCsv, reset } = usePapaParse<HailProductDefsCsvRow>({
        batchSize: 50,
        processBatch,
    });

    const onDrop = useCallback(
        (accepted: File[]) => {
            if (accepted.length > 0) {
                parseCsv(accepted[0]);
            }
        },
        [parseCsv]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv'],
        },
        multiple: false,
        disabled: importer.state === 'parsing' || importer.state === 'running',
    });

    const handleClose = () => {
        if (importer.state === 'complete') {
            onComplete();
        } else {
            onClose();
        }
        reset();
    };

    const progress =
        importer.state === 'running' || importer.state === 'complete'
            ? importer.rowCount > 0
                ? (importer.importCount / importer.rowCount) * 100
                : 0
            : 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload Hail Product Definitions CSV</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Expected columns: <strong>Hail Product</strong>,{' '}
                        <strong>Definition</strong>,{' '}
                        <strong>Hail Product Alias</strong> or{' '}
                        <strong>Coverage Alias</strong> (comma-separated)
                    </Alert>

                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : '#ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor:
                                importer.state === 'running'
                                    ? 'default'
                                    : 'pointer',
                            bgcolor: isDragActive
                                ? 'action.hover'
                                : 'transparent',
                            transition: 'all 0.2s',
                        }}
                    >
                        <input {...getInputProps()} />
                        <UploadIcon
                            sx={{
                                fontSize: 40,
                                color: 'text.secondary',
                                mb: 1,
                            }}
                        />
                        <Typography>
                            {isDragActive
                                ? 'Drop the CSV file here...'
                                : 'Drag & drop a CSV file here, or click to select'}
                        </Typography>
                    </Box>

                    {importer.state === 'parsing' && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                Parsing CSV...
                            </Typography>
                        </Stack>
                    )}

                    {(importer.state === 'running' ||
                        importer.state === 'complete') && (
                        <Box>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 0.5 }}
                            >
                                <Typography variant="body2">
                                    {importer.state === 'complete'
                                        ? 'Upload complete!'
                                        : `Importing ${importer.importCount} of ${importer.rowCount} rows...`}
                                </Typography>
                                {importer.remainingTime != null && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        ~
                                        {Math.ceil(
                                            importer.remainingTime / 1000
                                        )}
                                        s remaining
                                    </Typography>
                                )}
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                }}
                            />
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Typography
                                    variant="body2"
                                    color="success.main"
                                >
                                    {'\u2713'} {importer.importCount} imported
                                </Typography>
                                {importer.errorCount > 0 && (
                                    <Typography
                                        variant="body2"
                                        color="error.main"
                                    >
                                        {'\u2717'} {importer.errorCount} errors
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {importer.state === 'error' && (
                        <Alert severity="error">
                            Failed to parse CSV: {importer.error.message}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {importer.state === 'complete' ? 'Done' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default HailProductDefsPage;
